import CONFIG from "../config/config";
import _ from "lodash";
import SYSTEM_PUBSUB from "../utils/systemPubSub";
import { first, filter, map } from "rxjs/operators";
import { interval } from "rxjs";
import log2 from "../utils/log2";
import { getAppSyncApiKey } from "../api/appsync";
import { deviceId } from "../utils/session";

const log = log2("PushNotificationService");
/**
 *
 * @type {ServiceWorkerRegistration}
 */
let serviceWorkerRegistration = null;
/**
 *
 * @type {PushSubscription}
 */
let subscription = null;
let isSafeLoggedOut = false;

let swRegistered = false;
const waitSwRegistered = () => {
    if (swRegistered) {
        return Promise.resolve(true);
    } else {
        return SYSTEM_PUBSUB.stream
            .pipe(
                filter((e) => e.type === SYSTEM_PUBSUB.MESSAGE_TYPES.SW_REGISTERED),
                first()
            )
            .toPromise()
            .then((x) => {
                swRegistered = true;
                return true;
            });
    }
};
const registeredTopics = {};
let waitingTopics = [];

let appSyncApiKey = null;

/**
 *
 * @param serviceWorkerRegistration:ServiceWorkerRegistration
 */
const waitActiveState = (serviceWorkerRegistration) => {
    if (_.get(serviceWorkerRegistration, "active.state") === "activated") {
        return Promise.resolve(true);
    } else {
        return interval(100)
            .pipe(
                map(() => _.get(serviceWorkerRegistration, "active.state")),
                filter((state) => state === "activated"),
                first()
            )
            .toPromise();
    }
};
const registerServiceWorker = async () => {
    try {
        log("registerServiceWorker called", serviceWorkerRegistration);
        if (serviceWorkerRegistration) {
            log("Service Worker already registered");
            return;
        }
        log("Registering service worker...");
        serviceWorkerRegistration = await navigator.serviceWorker.register("static/service-worker.js");
        log("Registering service instance", {
            active: serviceWorkerRegistration.active,
            installing: serviceWorkerRegistration.installing,
            waiting: serviceWorkerRegistration.waiting,
        });

        serviceWorkerRegistration.addEventListener("updatefound", () => {
            try {
                serviceWorkerRegistration.update();
            } catch (e) {}
            log("updatefound");
            const newWorker = serviceWorkerRegistration.installing;
            newWorker.addEventListener("statechange", () => {
                log("NewWorker state change", newWorker.state);
                if (newWorker.state === "installed") {
                    log("New worker installed");
                }
            });
        });

        await waitActiveState(serviceWorkerRegistration);
        log("Service Worker activation complete", {
            active: serviceWorkerRegistration.active,
            installing: serviceWorkerRegistration.installing,
            waiting: serviceWorkerRegistration.waiting,
        });
        log(
            "serviceWorkerRegistration.pushManager.permissionState()",
            serviceWorkerRegistration.pushManager.permissionState({
                userVisibleOnly: true,
            })
        );

        swRegistered = true;
        SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.SW_REGISTERED, {});
        if (notificationEnabled()) {
            promptNotificationPermission();
        }
        try {
            serviceWorkerRegistration.update();
        } catch (e) {}
    } catch (e) {
        log.error("registerServiceWorker exception", e);
    }
};
const notificationEnabled = () => {
    return Notification.permission === "granted";
};
const promptNotificationPermission = async () => {
    log("promptNotificationPermission called");
    serviceWorkerRegistration.pushManager
        .subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlB64ToUint8Array(CONFIG.WEB_PUSH.applicationServerKey),
        })
        .then((x) => {
            SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.NOTIFICATION_GRANTED, {});
            subscription = x;
            log("Subscribed to push notifications", subscription);
            if (waitingTopics.length > 0) {
                log("Subscribing to waiting topics", waitingTopics);
                waitingTopics.forEach(async (topic) => {
                    await subscribeToTopic(topic);
                });
            }
        })
        .catch((err) => {
            log.error("Error in promptNotificationPermission", err);
        });
};

const unsubscribeFromTopic = async (topic) => {
    const apiKey = appSyncApiKey || (await getAppSyncApiKey());
    appSyncApiKey = apiKey;
    log("firstly unsubscribe started", { topic });
    await fetch(CONFIG.WEB_PUSH.APP_SYNC_URL, {
        method: "POST",
        headers: { "x-api-key": apiKey },
        body: JSON.stringify({
            query: `mutation($topic: String) {unsubscribe(topic: $topic)}`,
            variables: {
                topic,
            },
        }),
    })
        .then((res) => res.json())
        .then((json) => {
            log("unsubscribe response", json);
            delete registeredTopics[topic];
        })
        .catch((err) => {
            console.error("unsubscribe error", err);
            return null;
        });
};
const subscribeToTopic = async (topic) => {
    const apiKey = appSyncApiKey || (await getAppSyncApiKey());
    appSyncApiKey = apiKey;
    log("subscribe started", { topic, WEB_PUSH: CONFIG.WEB_PUSH, subscription });
    fetch(CONFIG.WEB_PUSH.APP_SYNC_URL, {
        method: "POST",
        headers: { "x-api-key": apiKey },
        body: JSON.stringify({
            query: `mutation($topic: String, $subscription: String) {subscribe(topic: $topic, subscription: $subscription)}`,
            variables: {
                topic,
                subscription: JSON.stringify(subscription),
            },
        }),
    })
        .then((res) => res.json())
        .then((json) => {
            const data = JSON.parse(_.get(json, "data.subscribe") || "{}");
            registeredTopics[topic] = true;
            waitingTopics = _.without(waitingTopics, topic);
            log("subscribe response", json, data);
        })
        .catch((err) => {
            console.error("subscribe error", err);
        });
};

async function subscribe(siteId, userId, topic) {
    // topic = CONFIG.IS_DEVELOPMENT
    //     ? `${deviceId}_${siteId}_${userId}_${topic}`
    //     : `${CONFIG.ENVIRONMENT}_${siteId}_${userId}_${topic}`;
    topic = `${CONFIG.ENVIRONMENT}_${siteId}_${userId}_${topic}`;
    log("subscribe called", { siteId, userId, topic });
    if (!!registeredTopics[topic]) {
        log("Already subscribed to topic", topic);
        return;
    }
    log("waiting for service worker registration");
    await waitSwRegistered();
    if (isSafeLoggedOut && !subscription) {
        isSafeLoggedOut = false;
        await promptNotificationPermission();
    }
    log("service worker registration completed");
    if (!subscription) {
        log.warn("notification permission not granted, waiting for it");
        waitingTopics.push(topic);
    } else {
        await unsubscribeFromTopic(topic);
        await subscribeToTopic(topic);
    }
}

async function unsubscribe(siteId, userId, topic) {
    topic = `${CONFIG.ENVIRONMENT}_${siteId}_${userId}_${topic}`;
    if (!registeredTopics[topic]) {
        log("Not subscribed to topic", topic);
        return;
    }
    await unsubscribeFromTopic(topic);
}

async function unsubscribeFromAllTopic(siteId, userId) {
    const topics = Object.keys(registeredTopics).filter((topic) => {
        return topic.includes(`${CONFIG.ENVIRONMENT}_${siteId}_${userId}`);
    });
    log("unsubscribeFromAllTopic", { topics });
    topics.forEach((topic) => {
        unsubscribeFromTopic(topic);
    });
    if (subscription) {
        subscription
            .unsubscribe()
            .then(() => {
                log.info("unsubscribeFromAllTopic unsubscribed");
                isSafeLoggedOut = true;
                subscription = null;
            })
            .catch((err) => {
                log.error("unsubscribeFromAllTopic error", err);
            });
    }
}

function urlB64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/\-/g, "+").replace(/_/g, "/");

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

export const WEB_PUSH_NOTIFICATION_SERVICE = {
    subscribe,
    unsubscribe,
    unsubscribeFromAllTopic,
    registerServiceWorker,
    promptNotificationPermission,
};
